import React, { createContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Grid from "~/components/Grid";
import Filters from "~/components/Filters";
import FormLoading from "~/components/RegisterForm/FormLoading";
import FinancialFilters from "./FinancialFilters";
import { Section, errorText } from "./shared";

const InvoiceGridContext = createContext({});
const headers = [
  { title: "Student", name: "student_name", type: "text", filter: false },
  { title: "Registration", name: "registration_number", type: "text", filter: false },
  { title: "Invoice", name: "invoice_number", type: "text", filter: false },
  { title: "Due date", name: "due_date", type: "text", filter: false },
  { title: "Status", name: "status", type: "text", filter: false },
  { title: "Total", name: "total", type: "text", filter: false },
  { title: "Balance", name: "balance", type: "text", filter: false },
];
const limit = 20;
const defaultCriteria = JSON.stringify({ student_status: "In Class" });
const amount = value => Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = value => "$ " + amount(value);
const count = value => Number(value || 0).toLocaleString("en-US");
export const usDate = value => /^\d{4}-\d{2}-\d{2}/.test(String(value || "")) ? String(value).slice(5, 10) + "-" + String(value).slice(0, 4) : value || "";

export default function FinancialAudience({ value, onChange, filial, readonly, request, trackInvoices = false, onTrackInvoicesChange, preset = null }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [optionsError, setOptionsError] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const financial = value.financial;
  const criteria = JSON.stringify(financial || null);
  const [applied, setApplied] = useState(false);
  const showInvoices = !!financial && (applied || criteria !== defaultCriteria);

  useEffect(() => {
    if (readonly || (value.mode === "branch" && financial)) return;
    onChange({ mode: "branch", financial: financial || { student_status: "In Class" } });
  }, [readonly, value.mode, financial, onChange]);

  useEffect(() => { if (error) toast.error(error); }, [error]);
  useEffect(() => { if (optionsError) toast.error(optionsError); }, [optionsError]);
  useEffect(() => {
    const abort = new AbortController();
    request("get", "/messaging/campaigns/audience/options", undefined, abort.signal)
      .then(({ data }) => { if (!abort.signal.aborted) setPaymentMethods(data.paymentMethods || []); })
      .catch(failure => { if (!abort.signal.aborted) setOptionsError(errorText(failure)); });
    return () => abort.abort();
  }, [request]);
  useEffect(() => { setPage(1); }, [criteria]);

  useEffect(() => {
    const abort = new AbortController();
    setError("");
    if (!showInvoices) { setRows([]); setTotal(0); setSummary(null); return () => abort.abort(); }
    setLoading(true);
    const timer = setTimeout(() => {
      request("post", "/messaging/campaigns/audience/invoices", { audience_filters: { mode: "branch", financial: JSON.parse(criteria) } }, abort.signal, { page, limit })
        .then(({ data }) => {
          if (abort.signal.aborted) return;
          if (Number(data.filial_id) !== Number(filial.id)) throw new Error("Location changed. Reopen this campaign.");
          const maxPage = Math.max(1, Math.ceil(data.totalRows / limit));
          if (page > maxPage) { setPage(maxPage); return; }
          setRows(data.rows);
          setTotal(data.totalRows);
          setSummary(data.summary);
          setLoading(false);
        })
        .catch(failure => { if (!abort.signal.aborted) { setError(errorText(failure)); setLoading(false); } });
    }, 400);
    return () => { clearTimeout(timer); abort.abort(); };
  }, [criteria, page, request, filial.id, showInvoices]);

  const gridData = rows.map(row => ({ id: row.id, show: true, selectable: false, fields: [row.student_name, row.registration_number || "", "#" + row.invoice_number, usDate(row.due_date), row.status || "", money(row.total), money(row.balance)] }));

  return <section aria-label="Campaign audience" className="w-full">
    <Section title="Recipients">
      <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={trackInvoices} disabled={readonly} onChange={event => onTrackInvoicesChange?.(event.target.checked)} />Track invoices in this campaign</label>
      <FinancialFilters value={financial || {}} paymentMethods={paymentMethods} readonly={readonly || !!optionsError} preset={preset}
        onChange={(next, nextPreset) => { setApplied(true); onChange({ mode: "branch", financial: next }, nextPreset); }} />
      {optionsError && <p role="alert" className="text-xs text-red-700">Unable to load financial filter options: {optionsError}</p>}
      {!showInvoices && <p className="text-xs text-gray-600">Apply a filter to see the invoices that will receive this message.</p>}
      {showInvoices && summary && <p role="status" className="text-sm font-semibold">{count(summary.invoices)} invoices · {count(summary.students)} students · balance {money(summary.balance)}</p>}
      <InvoiceGridContext.Provider value={{ gridData, gridHeader: headers, page, setPage: next => setPage(Number(next)), pages: Math.max(1, Math.ceil(total / limit)), limit, gridDetails: { totalRows: total }, activeFilters: [], search: null, handleFilters: () => null,
        orderBy: { column: "student_name", asc: true }, setOrderBy: () => null, opened: null, handleOpened: () => null }}>
        {showInvoices && <><div className="w-full"><Filters Context={InvoiceGridContext} access={{}} showSearch={false} /></div>
        <div className="flex h-80 w-full min-w-0 min-h-0 flex-col overflow-hidden">{loading ? <FormLoading /> : <Grid Context={InvoiceGridContext} handleEdit={false} />}</div></>}
      </InvoiceGridContext.Provider>
    </Section>
  </section>;
}
